import React from 'react'

import MenuItem from 'components_new/atoms/Menu/MenuItem'
import TextField from 'components_new/atoms/TextField'

import FormulaBlock, {
  FORMULA_OPERAND_MIN_WIDTH
} from 'components_new/molecules/FormulaBlock'
import FormulaAddButton from 'components_new/molecules/FormulaBlock/AddButton'
import FormulaConstantField from 'components_new/molecules/FormulaBlock/ConstantField'
import FormulaOperandSelect from 'components_new/molecules/FormulaBlock/OperandSelect'
import FormulaTypeButton from 'components_new/molecules/FormulaBlock/TypeButton'

import { getOptions, operators, translateOperator } from './utils'

import {
  TMetricCalculationBlock,
  MetricCalculationBlockType,
  MetricCalculationOperation
} from 'types/GlobalMetric'
import { ICustomEconomyMetric } from 'types/GlobalMetric'
import { KpiVariable, KpiVariableWithChildren } from 'types/GlobalKpiVariable'
import { TFunction, useTranslation } from 'language/translator'

interface KpiFormulaBlockProps {
  block: TMetricCalculationBlock
  deleteBlock: (id: string, side: 'left' | 'right') => void
  formattedVariables: KpiVariableWithChildren[]
  editValue: (
    id: string,
    key: string,
    value:
      | ICustomEconomyMetric
      | KpiVariable
      | MetricCalculationOperation
      | string
  ) => void
  editType: (
    id: string,
    side: 'left' | 'right',
    value: MetricCalculationBlockType
  ) => void
  variables: KpiVariable[]
  templates: ICustomEconomyMetric[]
}

const parseBlock = (
  block: TMetricCalculationBlock,
  formattedVariables: KpiVariableWithChildren[],
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[],
  editType: (
    id: string,
    side: 'left' | 'right',
    value: MetricCalculationBlockType
  ) => void,
  editValue: (
    id: string,
    key: string,
    value: ICustomEconomyMetric | KpiVariable | string
  ) => void,
  deleteBlock: (id: string, side: 'left' | 'right') => void,
  t: TFunction,
  parentBlockId?: string,
  parentSide?: 'left' | 'right'
) => {
  const left = getSide(
    block,
    formattedVariables,
    variables,
    templates,
    'left',
    editType,
    editValue,
    deleteBlock,
    t
  )
  const right = getSide(
    block,
    formattedVariables,
    variables,
    templates,
    'right',
    editType,
    editValue,
    deleteBlock,
    t
  )

  return (
    <FormulaBlock
      left={left}
      onChangeOperation={(operation) =>
        editValue(block.id, 'operation', operation)
      }
      onRemove={
        parentBlockId && parentSide
          ? () => deleteBlock(parentBlockId, parentSide)
          : undefined
      }
      operation={block.operation}
      operations={operators.map((operator) => ({
        id: operator,
        title: translateOperator[operator]
      }))}
      right={right}
    />
  )
}

const getSide = (
  block: TMetricCalculationBlock,
  formattedVariables: KpiVariableWithChildren[],
  variables: KpiVariable[],
  templates: ICustomEconomyMetric[],
  side: 'left' | 'right',
  editType: (
    id: string,
    side: 'left' | 'right',
    value: MetricCalculationBlockType
  ) => void,
  editValue: (
    id: string,
    key: string,
    value: ICustomEconomyMetric | KpiVariable | string
  ) => void,
  deleteBlock: (id: string, side: 'left' | 'right') => void,
  t: TFunction
) => {
  const typeOptions = getOptions(
    (value: MetricCalculationBlockType) => editType(block.id, side, value),
    t
  )

  if (block[`${side}_type`] === MetricCalculationBlockType.NUMBER) {
    return (
      <FormulaConstantField
        onChange={(value) => editValue(block.id, `${side}_value`, value)}
        typeOptions={typeOptions}
        value={block[`${side}_value`] ?? ''}
      />
    )
  }

  if (block[`${side}_type`] === MetricCalculationBlockType.VARIABLE) {
    const variableId = block[
      `${side}_kpi_variable_id` as
        | 'left_kpi_variable_id'
        | 'right_kpi_variable_id'
    ]

    // Not the shared select: the account levels are a three-level hierarchy,
    // which the flat searchable list would lose.
    return (
      <TextField
        fullWidth={true}
        label={
          !variableId
            ? `${t('accounting', 'select_account_level')}...`
            : undefined
        }
        SelectProps={{
          IconComponent: () => (
            <FormulaTypeButton options={typeOptions} sx={{ mr: 1 }} />
          )
        }}
        select={true}
        size="small"
        onChange={(event) => {
          editValue(
            block.id,
            `${side}_kpi_variable_id`,
            event.target.value
          )
        }}
        value={variableId || ''}
        sx={{ minWidth: FORMULA_OPERAND_MIN_WIDTH, my: 0 }}
      >
        {formattedVariables.map((v0) => {
          const item = (
            <MenuItem key={v0.id} value={v0.id}>
              {v0.name}
            </MenuItem>
          )

          const children = v0.children.map((v1) => {
            const item2 = (
              <MenuItem key={v1.id} value={v1.id} sx={{ marginLeft: 2 }}>
                {v1.name}
              </MenuItem>
            )

            const children2 = v1.children.map((v2) => (
              <MenuItem key={v2.id} value={v2.id} sx={{ marginLeft: 4 }}>
                {v2.name}
              </MenuItem>
            ))

            return [item2, ...children2]
          })

          return [item, ...children]
        })}
      </TextField>
    )
  }

  if (block[`${side}_type`] === MetricCalculationBlockType.TEMPLATE) {
    return (
      <FormulaOperandSelect
        onChange={(id) => editValue(block.id, `${side}_kpi_template_id`, id)}
        options={templates.map((template) => ({
          id: template.id,
          title: template.title.sv
        }))}
        placeholder={`${t('metrics', 'select_metric')}...`}
        selectedId={
          block[
            `${side}_kpi_template_id` as
              | 'left_kpi_template_id'
              | 'right_kpi_template_id'
          ]
        }
        typeOptions={typeOptions}
      />
    )
  }

  if (block[`${side}_type`] === MetricCalculationBlockType.BLOCK) {
    const nestledBlock = block[
      `${side}_kpi_calculation_block`
    ] as TMetricCalculationBlock

    return parseBlock(
      nestledBlock,
      formattedVariables,
      variables,
      templates,
      editType,
      editValue,
      deleteBlock,
      t,
      block.id,
      side
    )
  }

  return <FormulaAddButton options={typeOptions} tooltip={t('global', 'add')} />
}

const KpiFormulaBlock = (props: KpiFormulaBlockProps) => {
  const {
    block,
    formattedVariables,
    editType,
    editValue,
    deleteBlock,
    variables,
    templates
  } = props

  const { t } = useTranslation()

  return parseBlock(
    block,
    formattedVariables,
    variables,
    templates,
    editType,
    editValue,
    deleteBlock,
    t
  )
}

export default KpiFormulaBlock
